import {
  buildEmptyManagedRuntimeState,
  normalizeManagedRuntimeState,
  stripManagedRuntimeStateForCheckpointPersistence,
} from "@/lib/thread-sync-replica-runtime"
import { openThreadReplicaDb, requestToPromise, transactionToPromise } from "@/lib/thread-replica-idb"
import {
  buildThreadReplicaEventSequenceRange,
  getThreadReplicaEventsStoreName,
} from "@/lib/thread-replica-event-store"
import {
  buildEmptyThreadReplicaMeta,
  EMPTY_THREAD_REPLICA_CURSOR,
  FIRST_THREAD_REPLICA_SEQUENCE,
  THREAD_REPLICA_EVENT_CONTRACT_VERSION,
  THREAD_REPLICA_CHECKPOINTS_COMPATIBLE_CURSOR_INDEX,
  THREAD_REPLICA_CHECKPOINTS_STORE,
  THREAD_REPLICA_META_STORE,
  THREAD_REPLICA_PROJECTOR_VERSION,
  type ThreadReplicaCheckpointRecord,
  type ThreadReplicaMetaRecord,
  type ThreadReplicaScope,
  normalizeThreadReplicaCheckpointRecord,
  normalizeThreadReplicaMetaRecord,
} from "@/lib/thread-replica-types"
import { readThreadReplicaMeta } from "@/lib/thread-replica-meta-store"

const THREAD_REPLICA_CHECKPOINT_IMPORT_DELETE_MAX_CURSOR = Number.MAX_SAFE_INTEGER
const DEFAULT_RUNTIME_CONVERSATION_ID = "default"

async function hasNonDefaultRuntimeConversationRows(args: ThreadReplicaScope & {
  db: IDBDatabase
}): Promise<boolean> {
  const transaction = args.db.transaction([getThreadReplicaEventsStoreName()], "readonly")
  const eventsStore = transaction.objectStore(getThreadReplicaEventsStoreName())
  const range = buildThreadReplicaEventSequenceRange({
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    lane: "runtime",
    fromSequence: FIRST_THREAD_REPLICA_SEQUENCE,
    toSequence: THREAD_REPLICA_CHECKPOINT_IMPORT_DELETE_MAX_CURSOR,
  })

  const hasNonDefault = await new Promise<boolean>((resolve, reject) => {
    const request = eventsStore.openCursor(range)
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"))
    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor) {
        resolve(false)
        return
      }

      const value = cursor.value as { conversationId?: unknown } | null
      const conversationId = typeof value?.conversationId === "string"
        ? value.conversationId.trim()
        : DEFAULT_RUNTIME_CONVERSATION_ID
      if (conversationId.length > 0 && conversationId !== DEFAULT_RUNTIME_CONVERSATION_ID) {
        resolve(true)
        return
      }
      cursor.continue()
    }
  })
  await transactionToPromise(transaction)
  return hasNonDefault
}

function doesRuntimeStateMatchCheckpointCursor(args: {
  cursor: number
  runtimeState: ThreadReplicaCheckpointRecord["runtimeState"] | undefined
}): boolean {
  if (!args.runtimeState) {
    return args.cursor === EMPTY_THREAD_REPLICA_CURSOR
  }

  if (args.cursor === EMPTY_THREAD_REPLICA_CURSOR) {
    return (
      (args.runtimeState.lastEventId ?? EMPTY_THREAD_REPLICA_CURSOR) ===
      EMPTY_THREAD_REPLICA_CURSOR
    )
  }

  const lastEventId = args.runtimeState.lastEventId
  const providerEventId = args.runtimeState.snapshot?.cursorState.providerEventId ?? null
  if (args.runtimeState.snapshot === null) {
    return lastEventId === args.cursor
  }

  return lastEventId === args.cursor && providerEventId === args.cursor
}

function buildThreadReplicaCheckpointKey(args: {
  scopeKey: string
  sessionId: string
  replicaEpoch: number
  cursor: number
}): [string, string, number, number] {
  return [args.scopeKey, args.sessionId, args.replicaEpoch, args.cursor]
}

function buildThreadReplicaCompatibleCheckpointKey(args: {
  scopeKey: string
  sessionId: string
  replicaEpoch: number
  eventContractVersion: number
  projectorVersion: number
  cursor: number
}): [string, string, number, number, number, number] {
  return [
    args.scopeKey,
    args.sessionId,
    args.replicaEpoch,
    args.eventContractVersion,
    args.projectorVersion,
    args.cursor,
  ]
}

export async function saveThreadReplicaCheckpoint(args: ThreadReplicaScope & {
  expectedEpoch: number
  cursor: number
  runtimeState?: ThreadReplicaCheckpointRecord["runtimeState"]
  projectorVersion?: number
  createdAtMs: number
  /**
   * Conversation id whose projection is captured in `runtimeState`.
   * Omitted/null is treated as the default conversation so legacy callers
   * remain correct, but foreground hot-path callers SHOULD pass an explicit id
   * because cross-conversation checkpoint reuse would corrupt the transcript.
   */
  conversationId?: string | null
}): Promise<{ status: "saved" } | { status: "stale_writer" | "storage_unavailable" }> {
  const normalizedCursor = Math.max(EMPTY_THREAD_REPLICA_CURSOR, Math.floor(args.cursor))

  // Belt-and-suspenders defense against the Bug 2 staleness mode
  // (browser-cache-terminal-state-signaling/bug-2-stale-checkpoint-from-cached-state):
  // `normalizeManagedRuntimeState` below forcibly rewrites `lastEventId` and
  // the opencode snapshot's `providerEventId` to equal `cursor`, which erases
  // any mismatch between the caller's claimed cursor and the cursor the
  // projection actually reflects. Every legitimate caller (foreground
  // post-capture, background warmer's `doesManagedRuntimeStateMatchCursor`
  // guard, test helper `computeReplicaManagedRuntimeStateForEvents`) already
  // produces a matched pair, so reject any caller that does not before the
  // normalization step hides the mismatch. Treat empty runtimeState
  // (lastEventId null/0) as only valid at the empty cursor; anything else is
  // almost certainly a stale-cachedState bug.
  const rawRuntimeLastEventId = args.runtimeState?.lastEventId ?? null
  const claimsNonEmptyCursor = normalizedCursor > EMPTY_THREAD_REPLICA_CURSOR
  const hasConcreteRuntimeLastEventId =
    typeof rawRuntimeLastEventId === "number" &&
    Number.isFinite(rawRuntimeLastEventId) &&
    rawRuntimeLastEventId > EMPTY_THREAD_REPLICA_CURSOR
  if (claimsNonEmptyCursor && hasConcreteRuntimeLastEventId && rawRuntimeLastEventId !== normalizedCursor) {
    return { status: "stale_writer" }
  }
  if (claimsNonEmptyCursor && args.runtimeState && !hasConcreteRuntimeLastEventId) {
    return { status: "stale_writer" }
  }

  const db = await openThreadReplicaDb()
  if (!db) {
    return { status: "storage_unavailable" }
  }

  const transaction = db.transaction([THREAD_REPLICA_CHECKPOINTS_STORE, THREAD_REPLICA_META_STORE], "readwrite")
  const metaStore = transaction.objectStore(THREAD_REPLICA_META_STORE)
  const checkpointStore = transaction.objectStore(THREAD_REPLICA_CHECKPOINTS_STORE)

  const currentMeta = normalizeThreadReplicaMetaRecord(
    await requestToPromise(metaStore.get([args.scopeKey, args.sessionId])),
  )
  const compatibleProjectorVersion = Math.max(
    THREAD_REPLICA_PROJECTOR_VERSION,
    Math.floor(args.projectorVersion ?? THREAD_REPLICA_PROJECTOR_VERSION),
  )
  if (
    !currentMeta ||
    currentMeta.replicaEpoch !== args.expectedEpoch ||
    normalizedCursor > currentMeta.localCursor
  ) {
    transaction.abort()
    return { status: "stale_writer" }
  }

  const checkpointKey = buildThreadReplicaCheckpointKey({
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    replicaEpoch: currentMeta.replicaEpoch,
    cursor: normalizedCursor,
  })
  const existingCheckpoint = normalizeThreadReplicaCheckpointRecord(
    await requestToPromise(checkpointStore.get(checkpointKey)),
  )
  const expectedConversationId = normalizeCheckpointConversationIdForMatch(args.conversationId)
  if (
    existingCheckpoint &&
    normalizeCheckpointConversationIdForMatch(existingCheckpoint.conversationId) !== expectedConversationId
  ) {
    // Checkpoint object-store keys predate runtime tabs and do not include the
    // conversation id. Until the IndexedDB schema is upgraded, never overwrite a
    // same-cursor checkpoint for a different tab; doing so would trade one fast
    // restore path for another and can make fully loaded tabs fall back to replay.
    await transactionToPromise(transaction)
    return { status: "saved" }
  }

  if (
    existingCheckpoint &&
    existingCheckpoint.eventContractVersion === currentMeta.eventContractVersion &&
    existingCheckpoint.projectorVersion === compatibleProjectorVersion &&
    existingCheckpoint.lastBaselineSequence === currentMeta.lastBaselineSequence &&
    normalizeCheckpointConversationIdForMatch(existingCheckpoint.conversationId) === expectedConversationId
  ) {
    if (!doesRuntimeStateMatchCheckpointCursor({
      cursor: normalizedCursor,
      runtimeState: args.runtimeState ?? buildEmptyManagedRuntimeState(),
    })) {
      transaction.abort()
      return { status: "stale_writer" }
    }

    if (currentMeta.lastCheckpointCursor < normalizedCursor) {
      metaStore.put({
        ...currentMeta,
        lastCheckpointCursor: normalizedCursor,
      })
      await transactionToPromise(transaction)
      return { status: "saved" }
    }

    await transactionToPromise(transaction)
    return { status: "saved" }
  }

  const normalizedRuntimeState = normalizeManagedRuntimeState({
    cursor: normalizedCursor,
    runtimeState: args.runtimeState ?? buildEmptyManagedRuntimeState(),
  })
  if (!doesRuntimeStateMatchCheckpointCursor({
    cursor: normalizedCursor,
    runtimeState: normalizedRuntimeState,
  })) {
    transaction.abort()
    return { status: "stale_writer" }
  }

  const persistedRuntimeState = stripManagedRuntimeStateForCheckpointPersistence(
    normalizedRuntimeState,
  )

  const record: ThreadReplicaCheckpointRecord = {
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    replicaEpoch: currentMeta.replicaEpoch,
    cursor: normalizedCursor,
    lastBaselineSequence: currentMeta.lastBaselineSequence,
    eventContractVersion: currentMeta.eventContractVersion,
    projectorVersion: compatibleProjectorVersion,
    coverage: currentMeta.coverage,
    baselineCoverage: currentMeta.baselineCoverage,
    agentType: persistedRuntimeState.agentType,
    runtimeState: persistedRuntimeState,
    createdAtMs: Math.max(EMPTY_THREAD_REPLICA_CURSOR, Math.floor(args.createdAtMs)),
    conversationId:
      typeof args.conversationId === "string" && args.conversationId.trim().length > 0
        ? args.conversationId.trim()
        : null,
  }

  checkpointStore.put(record)
  metaStore.put({
    ...currentMeta,
    lastCheckpointCursor: Math.max(currentMeta.lastCheckpointCursor, normalizedCursor),
  })
  await transactionToPromise(transaction)
  return { status: "saved" }
}

export async function importThreadReplicaRuntimeCheckpoint(args: ThreadReplicaScope & {
  cursor: number
  historyToken: string | null
  runtimeState: ThreadReplicaCheckpointRecord["runtimeState"]
  createdAtMs: number
  /**
   * Conversation id whose projection is captured in the imported runtime
   * state. R2-sidecar checkpoints currently cover only the default
   * conversation, so callers pass null/default. Persisting the tag keeps the
   * hydration reader's conversation match logic uniform.
   */
  conversationId?: string | null
}): Promise<
  | { status: "imported"; meta: ThreadReplicaMetaRecord }
  | { status: "stale_writer" | "storage_unavailable" }
> {
  const cursor = Math.max(EMPTY_THREAD_REPLICA_CURSOR, Math.floor(args.cursor))
  if (cursor <= EMPTY_THREAD_REPLICA_CURSOR) return { status: "stale_writer" }
  if (!doesRuntimeStateMatchCheckpointCursor({ cursor, runtimeState: args.runtimeState })) {
    return { status: "stale_writer" }
  }
  const db = await openThreadReplicaDb()
  if (!db) return { status: "storage_unavailable" }
  if (await hasNonDefaultRuntimeConversationRows({ ...args, db })) {
    return { status: "stale_writer" }
  }

  const transaction = db.transaction(
    [THREAD_REPLICA_CHECKPOINTS_STORE, THREAD_REPLICA_META_STORE, getThreadReplicaEventsStoreName()],
    "readwrite",
  )
  const metaStore = transaction.objectStore(THREAD_REPLICA_META_STORE)
  const checkpointStore = transaction.objectStore(THREAD_REPLICA_CHECKPOINTS_STORE)
  const eventsStore = transaction.objectStore(getThreadReplicaEventsStoreName())
  const currentMeta = normalizeThreadReplicaMetaRecord(
    await requestToPromise(metaStore.get([args.scopeKey, args.sessionId])),
  )
  if (currentMeta && currentMeta.localCursor > cursor) {
    transaction.abort()
    return { status: "stale_writer" }
  }
  const nowMs = Math.max(EMPTY_THREAD_REPLICA_CURSOR, Math.floor(args.createdAtMs))
  const nextEpoch = currentMeta ? currentMeta.replicaEpoch + 1 : buildEmptyThreadReplicaMeta({
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    nowMs,
  }).replicaEpoch
  const normalizedRuntimeState = normalizeManagedRuntimeState({
    cursor,
    runtimeState: args.runtimeState,
  })
  const persistedRuntimeState = stripManagedRuntimeStateForCheckpointPersistence(
    normalizedRuntimeState,
  )
  const nextMeta: ThreadReplicaMetaRecord = {
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    replicaEpoch: nextEpoch,
    eventContractVersion: THREAD_REPLICA_EVENT_CONTRACT_VERSION,
    projectorVersion: THREAD_REPLICA_PROJECTOR_VERSION,
    historyToken: typeof args.historyToken === "string" && args.historyToken.trim().length > 0
      ? args.historyToken
      : null,
    browserHistoryEpoch: null,
    localCursor: cursor,
    // The imported checkpoint is the durable anchor for `cursor`; no individual
    // runtime rows at or before that cursor are stored. Future rows start at the
    // next sequence, so hydrate must look up the checkpoint at
    // `oldestAvailableCursor - 1`.
    oldestAvailableCursor: cursor + FIRST_THREAD_REPLICA_SEQUENCE,
    coverage: "tail_window",
    baselineCoverage: "none",
    lastBaselineSequence: EMPTY_THREAD_REPLICA_CURSOR,
    lastCheckpointCursor: cursor,
    lastUpdatedAtMs: nowMs,
    lastValidatedAtMs: nowMs,
    lastResetReason: null,
  }
  // Imported R2 checkpoints create a new replica epoch, but event rows are keyed
  // only by session/lane/sequence (not epoch). Clear the old lane rows and prior
  // checkpoints in the same transaction so hydration cannot replay stale tail
  // rows from an older compacted replica after the imported checkpoint wins.
  await requestToPromise(eventsStore.delete(buildThreadReplicaEventSequenceRange({
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    lane: "runtime",
    fromSequence: FIRST_THREAD_REPLICA_SEQUENCE,
    toSequence: THREAD_REPLICA_CHECKPOINT_IMPORT_DELETE_MAX_CURSOR,
  })))
  await requestToPromise(eventsStore.delete(buildThreadReplicaEventSequenceRange({
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    lane: "baseline",
    fromSequence: FIRST_THREAD_REPLICA_SEQUENCE,
    toSequence: THREAD_REPLICA_CHECKPOINT_IMPORT_DELETE_MAX_CURSOR,
  })))
  await requestToPromise(checkpointStore.delete(IDBKeyRange.bound(
    buildThreadReplicaCheckpointKey({
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      replicaEpoch: EMPTY_THREAD_REPLICA_CURSOR,
      cursor: EMPTY_THREAD_REPLICA_CURSOR,
    }),
    buildThreadReplicaCheckpointKey({
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      replicaEpoch: THREAD_REPLICA_CHECKPOINT_IMPORT_DELETE_MAX_CURSOR,
      cursor: THREAD_REPLICA_CHECKPOINT_IMPORT_DELETE_MAX_CURSOR,
    }),
  )))

  checkpointStore.put({
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    replicaEpoch: nextEpoch,
    cursor,
    lastBaselineSequence: EMPTY_THREAD_REPLICA_CURSOR,
    eventContractVersion: THREAD_REPLICA_EVENT_CONTRACT_VERSION,
    projectorVersion: THREAD_REPLICA_PROJECTOR_VERSION,
    coverage: "tail_window",
    baselineCoverage: "none",
    agentType: persistedRuntimeState.agentType,
    runtimeState: persistedRuntimeState,
    createdAtMs: nowMs,
    conversationId:
      typeof args.conversationId === "string" && args.conversationId.trim().length > 0
        ? args.conversationId.trim()
        : null,
  } satisfies ThreadReplicaCheckpointRecord)
  metaStore.put(nextMeta)
  await transactionToPromise(transaction)
  return { status: "imported", meta: nextMeta }
}

/**
 * Conversation ids are normalized to a stable string here so legacy rows
 * (conversationId === null) compare equal to the canonical default tag. This
 * keeps pre-multi-conversation checkpoints usable for default hydration while
 * rejecting them for side-chat hydration.
 */
const DEFAULT_CHECKPOINT_CONVERSATION_ID = "default"

function normalizeCheckpointConversationIdForMatch(value: string | null | undefined): string {
  if (typeof value !== "string") return DEFAULT_CHECKPOINT_CONVERSATION_ID
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : DEFAULT_CHECKPOINT_CONVERSATION_ID
}

export async function readNewestThreadReplicaCheckpoint(args: ThreadReplicaScope & {
  replicaEpoch: number
  targetCursor: number
  eventContractVersion: number
  projectorVersion?: number
  /**
   * Active conversation id requesting the checkpoint. Hydration skips any
   * stored checkpoint whose projection was captured for a different
   * conversation, because those projections are not interchangeable. Omitting
   * this is equivalent to asking for the default conversation.
   */
  conversationId?: string | null
}): Promise<ThreadReplicaCheckpointRecord | null> {
  const db = await openThreadReplicaDb()
  if (!db) {
    return null
  }

  const compatibleProjectorVersion = Math.max(
    THREAD_REPLICA_PROJECTOR_VERSION,
    Math.floor(args.projectorVersion ?? THREAD_REPLICA_PROJECTOR_VERSION),
  )
  const transaction = db.transaction(THREAD_REPLICA_CHECKPOINTS_STORE, "readonly")
  const store = transaction.objectStore(THREAD_REPLICA_CHECKPOINTS_STORE)
  const targetCursor = Math.max(EMPTY_THREAD_REPLICA_CURSOR, Math.floor(args.targetCursor))
  const compatibleIndex = store.indexNames.contains(THREAD_REPLICA_CHECKPOINTS_COMPATIBLE_CURSOR_INDEX)
    ? store.index(THREAD_REPLICA_CHECKPOINTS_COMPATIBLE_CURSOR_INDEX)
    : null
  const request = compatibleIndex
    ? compatibleIndex.openCursor(
      IDBKeyRange.bound(
        buildThreadReplicaCompatibleCheckpointKey({
          scopeKey: args.scopeKey,
          sessionId: args.sessionId,
          replicaEpoch: args.replicaEpoch,
          eventContractVersion: args.eventContractVersion,
          projectorVersion: compatibleProjectorVersion,
          cursor: EMPTY_THREAD_REPLICA_CURSOR,
        }),
        buildThreadReplicaCompatibleCheckpointKey({
          scopeKey: args.scopeKey,
          sessionId: args.sessionId,
          replicaEpoch: args.replicaEpoch,
          eventContractVersion: args.eventContractVersion,
          projectorVersion: compatibleProjectorVersion,
          cursor: targetCursor,
        }),
      ),
      "prev",
    )
    : store.openCursor(
      IDBKeyRange.bound(
        buildThreadReplicaCheckpointKey({
          scopeKey: args.scopeKey,
          sessionId: args.sessionId,
          replicaEpoch: args.replicaEpoch,
          cursor: EMPTY_THREAD_REPLICA_CURSOR,
        }),
        buildThreadReplicaCheckpointKey({
          scopeKey: args.scopeKey,
          sessionId: args.sessionId,
          replicaEpoch: args.replicaEpoch,
          cursor: targetCursor,
        }),
      ),
      "prev",
    )
  const compatibleRecord = await new Promise<ThreadReplicaCheckpointRecord | null>((resolve, reject) => {
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"))
    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor) {
        resolve(null)
        return
      }

      const record = normalizeThreadReplicaCheckpointRecord(cursor.value)
      const expectedConversationId = normalizeCheckpointConversationIdForMatch(args.conversationId)
      if (
        record &&
        record.eventContractVersion === args.eventContractVersion &&
        record.projectorVersion === compatibleProjectorVersion &&
        normalizeCheckpointConversationIdForMatch(record.conversationId) === expectedConversationId
      ) {
        resolve(record)
        return
      }

      cursor.continue()
    }
  })
  await transactionToPromise(transaction)
  return compatibleRecord
}

export async function clearThreadReplicaCheckpoints(args: ThreadReplicaScope): Promise<void> {
  const meta = await readThreadReplicaMeta(args)
  const db = await openThreadReplicaDb()
  if (!db || !meta) {
    return
  }

  const transaction = db.transaction(THREAD_REPLICA_CHECKPOINTS_STORE, "readwrite")
  const store = transaction.objectStore(THREAD_REPLICA_CHECKPOINTS_STORE)
  store.delete(
    IDBKeyRange.bound(
      buildThreadReplicaCheckpointKey({
        scopeKey: args.scopeKey,
        sessionId: args.sessionId,
        replicaEpoch: EMPTY_THREAD_REPLICA_CURSOR,
        cursor: EMPTY_THREAD_REPLICA_CURSOR,
      }),
      buildThreadReplicaCheckpointKey({
        scopeKey: args.scopeKey,
        sessionId: args.sessionId,
        replicaEpoch: meta.replicaEpoch + 1,
        cursor: Number.MAX_SAFE_INTEGER,
      }),
    ),
  )
  await transactionToPromise(transaction)
}

export function buildThreadReplicaCheckpointFromMeta(args: {
  meta: ThreadReplicaMetaRecord
  runtimeState: ThreadReplicaCheckpointRecord["runtimeState"]
  createdAtMs: number
  conversationId?: string | null
}): ThreadReplicaCheckpointRecord {
  const runtimeState = stripManagedRuntimeStateForCheckpointPersistence(args.runtimeState)

  return {
    scopeKey: args.meta.scopeKey,
    sessionId: args.meta.sessionId,
    replicaEpoch: args.meta.replicaEpoch,
    cursor: args.meta.localCursor,
    lastBaselineSequence: args.meta.lastBaselineSequence,
    eventContractVersion: args.meta.eventContractVersion,
    projectorVersion: args.meta.projectorVersion,
    coverage: args.meta.coverage,
    baselineCoverage: args.meta.baselineCoverage,
    agentType: runtimeState.agentType,
    runtimeState,
    createdAtMs: args.createdAtMs,
    conversationId:
      typeof args.conversationId === "string" && args.conversationId.trim().length > 0
        ? args.conversationId.trim()
        : null,
  }
}
